import React, { useCallback, useMemo } from "react"
import { Helmet } from "react-helmet"
import { StaticQuery, graphql } from "gatsby"
import { languages, getLanguage } from "Utils/Language"
import { getLogoSrc, isBrowser } from "Utils/Helpers"

function Seo({ page = {}, translationSlugs = {}, meta = {}, data }) {
  const { code, hreflang, siteName } = getLanguage()

  const {
    title: pageTitle,
    featured_media,
    yoast_head_json,
    content,
  } = page || {}

  const { source_url: featuredMediaSrc = "" } = featured_media || {}

  const get_P_Element = str => {
    const parser = new DOMParser()
    const doc = parser.parseFromString(str, "text/html")
    const pElement = doc.querySelector("p")

    if (!pElement) return ""

    const textContent = pElement.textContent || ""
    const words = textContent.split("")
    const descriptionContent = words
      .slice(0, process.env.GATSBY_META_DESCRIPTION_MAX_CHAR)
      .join("")
    return descriptionContent
  }

  const {
    title: metaTitle,
    description: metaDescription,
    facebook_title: fbTitle,
    facebook_description: fbDescription,
    twitter_title: twTitle,
    twitter_description: twDescription,
  } = yoast_head_json || {}

  const title = metaTitle || pageTitle
  const titleTemplate = metaTitle ? "" : `%s - ${siteName}`
  const formattedTitle = useMemo(
    () => (typeof title === "string" ? title : title.rendered),
    [title],
  )

  const [hrefLanugage, hrefCountry] = hreflang.split("-")

  const getOgImage = useCallback(() => getLogoSrc(code), [code])

  const getOgUrl = () => {
    if (!isBrowser) return null

    const {
      location: { href, pathname, origin },
    } = window
    if (href.includes("?form")) {
      return `${origin}${pathname}`
    }

    return href
  }
  const isBlogUrl = getOgUrl()?.split("/").includes("blog")
  const metaData = useMemo(
    () => [
      {
        property: "og:title",
        content: meta.title || fbTitle || formattedTitle || "",
      },
      { property: "og:type", content: "article" },
      {
        property: "description",
        content:
          metaDescription || (isBlogUrl ? get_P_Element(content.rendered) : ""),
      },
      {
        property: "og:image",
        content: meta.image || featuredMediaSrc || getOgImage(),
      },
      {
        property: "og:description",
        content: fbDescription || metaDescription || "",
      },
      { property: "og:locale", content: hreflang },
      { property: "og:url", content: getOgUrl() },
      { name: "twitter:card", content: "summary_large_image" },
      {
        name: "twitter:title",
        content: meta.title || twTitle || formattedTitle || "",
      },
      {
        property: "twitter:image",
        content: meta.image || featuredMediaSrc || getOgImage(),
      },
    ],
    [
      meta.title,
      fbTitle,
      formattedTitle,
      metaDescription,
      meta.image,
      featuredMediaSrc,
      content.rendered,
      isBlogUrl,
      fbDescription,
      getOgImage,
      hreflang,
      twTitle,
    ],
  )

  if (metaTitle) metaData.push({ name: "title", content: metaTitle })
  if (metaDescription)
    metaData.push({ name: "description", content: metaDescription })
  if (fbDescription)
    metaData.push({ property: "og:description", content: fbDescription })
  if (twDescription)
    metaData.push({ property: "twitter:description", content: twDescription })

  const hreflangs = useMemo(() => {
    const rel = "alternate"
    const quebec = "qc"
    const filter = code.includes(quebec)

    return Object.keys(languages)
      .filter(key => {
        const { code } = languages[key]

        if (!translationSlugs[code] && translationSlugs[code] !== "/")
          return false
        if (filter) return key.includes(quebec)

        return !key.includes(quebec)
      })
      .map(key => {
        const { url, base, hreflang, code } = languages[key]
        const localeSlug = translationSlugs[code].replace("/", "")
          ? `/${translationSlugs[code]}`
          : ""
        const hrefToUse = `${base}${url}${localeSlug}`.replace("homepage", "")
        const href = `${
          hrefToUse.endsWith("/") ? hrefToUse : `${hrefToUse}/`
        }`.replace("homepage/", "")

        return {
          rel,
          href,
          hreflang: hreflang.replace("_", "-"),
        }
      })
  }, [code, translationSlugs])

  const getCanonicalUrl = () => (isBrowser ? window.location.href : null);

  const pageModifiedDate = page?.modified || "";
  const page_title = page?.title?.rendered || "Bath Fitter";
  
  const OrganizationalObj = (page) => ({
      "@context": "https://schema.org",
      "@type": "Corporation",
      "name": "Bath Fitter",
      "url": "https://www.bathfitter.com",
      "logo": "https://bathfittercom-prod-backend.azurewebsites.net/wp-content/uploads/2020/09/logo-3.svg",
      "sameAs": [
        "https://facebook.com/OfficialBathFitter",
        "https://www.youtube.com/channel/UCuESTAuQjUunOjuhdPOrvZw",
        "https://www.pinterest.ca/bathfitter/",
        "https://www.instagram.com/bathfitter/",
        "https://www.linkedin.com/company/bath-fitter/"
      ]
  });
  
  const FAQOBJ = (page) => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
        {
            "@type": "Question",
            "name": "Why have a Bath Fitter acrylic bathtub or shower liner installed, instead of removing the old tub?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "A Bath Fitter acrylic liner looks exactly the same as installing a  with demolition renovation, and is installed right over existing tiling in just one day. With Bath Fitter, you get the same quality remodel simpler and faster. On the other hand, removing a bathtub is a difficult and messy job. The process can damage flooring, bathroom walls, and plumbing, all while putting your bathroom out of service for many days or even weeks."
            }
        },
        {
            "@type": "Question",
            "name": "Why install a Bath Fitter bathroom wall instead of installing tile?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "Bath Fitter walls are uniquely one-piece and installed over your existing wall in just one day. Our seamless walls eliminate grout, and wipe clean easily. Plus they come in plenty of tile patterns if tile is your chosen aesthetic! If necessary, we repair any damaged areas in your wall first to ensure a solid, secure base to surround your new tub or shower. That's one reason we guarantee our seamless walls with a lifetime warranty . If you want to keep your existing tile, we can always install a new tub without a new wall system."
            }
        },
        {
            "@type": "Question",
            "name": "Can you make my tub deeper by installing a Bath Fitter liner?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "While we cannot make your existing tub deeper by installing a liner, we do carry a line of luxurious deep soaker tubs. Our experts can provide you with all available options during our free in-home consultation."
            }
        },
        {
            "@type": "Question",
            "name": "Why is Bath Fitter a better option for my bathroom remodel than bathtub resurfacing or bathtub refinishing?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "A Bath Fitter high-gloss acrylic tub or shower liner is guaranteed for as long as you own your home. It's a faster, simpler way to get a high-quality, permanent renovation. On the other hand, repainting or resurfacing is a short-term option that will need to be done again within two to five years. The repeat costs add up quickly."
            }
        },
        {
            "@type": "Question",
            "name": "Can Bath Fitter install if there is a window in the bathtub/shower area?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "Installing around a window is not a problem for our Bath Fitter team! We make every wall and tub custom to fit your exact home."
            }
        },
        {
            "@type": "Question",
            "name": "How long will a Bath Fitter acrylic bathtub or shower solution last?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "Bath Fitter liners are not a temporary fix, they are a remodel we guarantee will last a lifetime. Our products are made to look just like new for longer than many traditional demolition renovation options. Before we install, a Bath Fitter expert will do any necessary cleaning and repairs to your current system to make sure there's a strong foundation for your bath remodel to last longer. Unlike porcelain, fiberglass or enamel surfaces, Bath Fitter high-gloss acrylic liners wipe away clean so easily they are virtually maintenance-free. Plus, our unique seamless wall system eliminates grout. And because acrylic is colored all the way through, minor scratches won't even show."
            }
        },
        {
            "@type": "Question",
            "name": "What type of warranty does Bath Fitter offer?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "Homeowners can benefit from Bath Fitter\u2019s acrylic bathtub and shower liners, which come with a lifetime guarantee. Bath Fitter-manufactured acrylic bathtub and shower liners homeowners purchased after June 5, 2023, include a transferable lifetime warranty that covers manufacturing defects. Rest assured, our products are built to last. Subject to limitations. See your location or www.bathfitter.com/us-en/lifetime-warranty/ for details."
            }
        }
    ]
  });
    
  const createBlogDataScript = (page) => ({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "name" : "Bath Fitter",
      "headline": page_title|| "",
      "datePublished":pageModifiedDate || "",
      "description": page?.excerpt || "",
      "articleBody": page?.post_content || "",
      "image": page?.featuredImage || "https://bathfittercom-prod-backend.azurewebsites.net/wp-content/uploads/2020/09/logo-3.svg",
      "author": {
        "@type": "Person",
        "name": page?.author || ""
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": page?.link || "https://www.bathfitter.com"
      },
  });
  


  let  structuredDataScript = OrganizationalObj(page);

  if (page_title === "FAQs") {
    structuredDataScript = FAQOBJ(page);
  }
  
  if (page?.type === "post") {
    structuredDataScript = createBlogDataScript(page);
  }
  return (
    <Helmet
      htmlAttributes={{ lang: `${hrefLanugage}-${hrefCountry.toUpperCase()}` }}
      title={formattedTitle}
      titleTemplate={titleTemplate}
      meta={metaData}
      link={[
        ...hreflangs,
        { rel: "canonical", href: getCanonicalUrl(), hreflang: hreflang },
      ]}
    >
      <script type="application/ld+json">
        {JSON.stringify(structuredDataScript)}
      </script>
    </Helmet>
  );
}

const SEOContainer = props => (
  <StaticQuery
    query={graphql`
      query {
        logo: allWidgets(
          filter: { parent_sidebar: { eq: "bathfitter-logo" } }
        ) {
          nodes {
            id
            rendered
            language
          }
        }
      }
    `}
    render={data => <Seo {...props} data={data} />}
  />
)

export default SEOContainer
